import React, { useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import stringify from 'fast-json-stable-stringify';
import SubscriptionCallToActionComponent
  from '@fuww/library/src/SubscriptionCallToAction';
import messages from '../../lib/messages.json';
import LinkAndAnchor from '../LinkAndAnchor';
import { saveRouterState } from '../../lib/routerState';
import createAnalyticsEvent
  from '../../lib/mutations/createAnalyticsEvent.graphql';
import {
  AccountType,
  AnalyticsEventType,
  BundlesQuery,
  BundlesQueryVariables,
} from '../../lib/graphql/api/graphql';
import { useSiteContext } from '../SiteContext';
import getArticleSubjectType from '../../lib/getArticleSubjectType';
import { UserContext } from '../UserProvider';
import withQuery from '../../lib/withQuery';
import bundlesQuery from '../../lib/queries/dashboard/bundlesQuery.graphql';
import subscriptionBundleNames from '../../lib/subscriptionBundleNames';

interface SubscriptionCallToActionProperties {
  data: BundlesQuery | undefined;
}

export const SubscriptionCallToAction = ({
  data,
}: SubscriptionCallToActionProperties) => {
  const intl = useIntl();
  const router = useRouter();
  const [createPageview] = useMutation(createAnalyticsEvent);
  const { route, query } = useRouter();
  const { query: { id } } = useRouter();
  const subjectId = id ? `${id}` : null;

  const {
    prefix,
    hasLocalNews,
    url,
    newsboard,
    locale,
  } = useSiteContext();

  const stringifiedRouter = stringify(router);
  useEffect(() => {
    saveRouterState(JSON.parse(stringifiedRouter));
  }, [stringifiedRouter]);

  const formattedPrices = useMemo(() => {
    const placeholder = {
      monthly: '_',
      annual: '_',
      annualSavings: '_',
      annualMonthlyEquivalent: '_',
    };

    if (!data?.bundles) {
      return placeholder;
    }

    const monthlyBundle = data.bundles.find(
      ({ name }) => name === subscriptionBundleNames.monthly,
    );
    const yearlyBundle = data.bundles.find(
      ({ name }) => name === subscriptionBundleNames.yearly,
    );

    const monthlyAmount = monthlyBundle?.price.amount;
    const annualAmount = yearlyBundle?.price.amount;

    if (!monthlyAmount || !annualAmount) {
      return placeholder;
    }

    const monthly = Number(monthlyAmount);
    const annual = Number(annualAmount);

    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return {
      monthly: formatter.format(monthly),
      annual: formatter.format(annual),
      annualSavings: formatter.format(monthly * 12 - annual),
      annualMonthlyEquivalent: formatter.format(annual / 12),
    };
  }, [data, locale]);

  const { user } = useContext(UserContext);

  // eslint-disable-next-line no-underscore-dangle
  const userId = user?._id;

  const canonicalRouteQuery = stringify({ route, query });

  useEffect(() => {
    createPageview({
      variables: {
        type: AnalyticsEventType.EVENT,
        url,
        title: 'subscription callToAction',
        subjectType: id ? getArticleSubjectType(prefix, hasLocalNews) : null,
        subjectId: subjectId || null,
        payload: JSON.stringify({
          newsboard,
          userId,
        }),
      },
    });
    // don't re-run it even when data changes
    // only route and query changes should trigger this hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalRouteQuery]);

  return (
    <SubscriptionCallToActionComponent
      callToActionMessage={intl.formatMessage(
        messages['subscriptionCallToAction.callToAction'],
      )}
      sellingPoints={[
        intl.formatMessage(
          messages['subscriptionCallToAction.exclusiveAccess'],
        ),
        intl.formatMessage(
          messages['subscriptionCallToAction.insiderForecasts'],
        ),
        intl.formatMessage(
          messages['subscriptionCallToAction.directInsights'],
        ),
      ]}
      youCanCancelMessage={intl.formatMessage(
        messages['subscriptionCallToAction.youCanCancel'],
      )}
      startReadingMessage={intl.formatMessage(
        messages['subscriptionCallToAction.startReading'],
      )}
      complimentaryAccessMessage={intl.formatMessage(
        messages['subscriptionCallToAction.complimentaryAccess'],
      )}
      subscriptionPriceMessage={intl.formatMessage(
        messages['subscriptionCallToAction.subscriptionPrice'],
        {
          monthlyAmount: formattedPrices.monthly,
          yearlyAmount: formattedPrices.annualMonthlyEquivalent,
        },
      )}
      yearlyMessage={intl.formatMessage(
        messages['subscriptionFlow.asideContent.yearly'],
      )}
      monthlyMessage={intl.formatMessage(
        messages['subscriptionFlow.asideContent.monthly'],
      )}
      savePerYearMessage={intl.formatMessage(
        messages['subscriptionCallToAction.savePerYear'],
        { amount: formattedPrices.annualSavings },
      )}
      perMonthAbbreviationMessage={intl.formatMessage(
        messages['subscriptionCallToAction.perMonthAbbreviation'],
      )}
      billedAnnuallyMessage={intl.formatMessage(
        messages['subscriptionCallToAction.billedAnnually'],
        { amount: formattedPrices.annual },
      )}
      bestValueMessage={intl.formatMessage(
        messages['subscriptionCallToAction.bestValue'],
      )}
      billedMonthlyMessage={intl.formatMessage(
        messages['subscriptionCallToAction.billedMonthly'],
      )}
      annualBillingMonthlyEquivalent={formattedPrices.annualMonthlyEquivalent}
      monthlyBillingPrice={formattedPrices.monthly}
      LinkComponent={LinkAndAnchor}
      onClick={() => {
        saveRouterState(router);
      }}
    />
  );
};

const SubscriptionCallToActionWithQuery = withQuery<
  BundlesQuery,
  BundlesQueryVariables,
  true
>(SubscriptionCallToAction, { renderAlways: true });

const SubscriptionComponent = () => (
  <SubscriptionCallToActionWithQuery
    query={bundlesQuery}
    variables={{
      accountType: AccountType.PERSONAL,
    }}
    errorMessage="Error fetching bundle prices"
    loader={null}
  />
);

export default SubscriptionComponent;
